import { useMemo } from "react";
import { dateLocale, t } from "@/lib/i18n";
import { useLogStream } from "@/hooks/useLogStream";
import { LevelBadge } from "@/components/logs/LevelBadge";
import { EMPTY_FILTERS, type LogEntry, type LogLevel } from "@/lib/types";

export function EventFeed({ enabled, minLevel }: { enabled: boolean; minLevel?: LogLevel | null }) {
  const filters = useMemo(
    () => ({
      ...EMPTY_FILTERS,
      limit: 30,
      levelMode: "minimum" as const,
      level: minLevel ?? undefined,
    }),
    [minLevel],
  );
  const { logs, state } = useLogStream(filters, enabled);

  return (
    // `min-h-0` ist Pflicht damit flex-1+overflow-auto auf dem <ul> greift.
    // Ohne diese Zeile rechnet der Browser min-height: auto und das Feed-Element
    // wächst über seinen Flex-Container hinaus (= riesige Seite).
    // `max-h-[80vh]` als Sicherheitsnetz für Layouts in denen der Sibling kürzer
    // ist als der Feed-Content (z.B. wenn keine Map ausgewählt ist).
    <aside className="flex w-[300px] shrink-0 flex-col rounded-lg border border-border bg-card min-h-0 max-h-[80vh] self-stretch">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t("Live Event Feed")}
        </span>
        <span
          className={
            "inline-flex h-1.5 w-1.5 rounded-full " +
            (state === "open" ? "bg-success" : "bg-muted-foreground/40")
          }
          aria-label={state === "open" ? t("verbunden") : t("nicht verbunden")}
        />
      </div>
      <ul className="flex-1 divide-y divide-border overflow-y-auto min-h-0">
        {logs.length === 0 ? (
          <li className="px-3 py-6 text-center text-xs text-muted-foreground">
            {t("Keine Events.")}
          </li>
        ) : (
          logs.slice(0, 30).map((log, i) => <FeedRow key={log.id ?? `${log.timestamp}-${i}`} log={log} />)
        )}
      </ul>
    </aside>
  );
}

function FeedRow({ log }: { log: LogEntry }) {
  const ts = new Date(log.timestamp).toLocaleTimeString(dateLocale(), {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  return (
    <li className="animate-in slide-in-from-right-2 fade-in px-3 py-2 text-xs duration-300">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground tabular-nums">{ts}</span>
        <LevelBadge level={log.level} />
      </div>
      <div className="mt-0.5 break-words font-mono leading-snug">{log.message}</div>
      <div className="mt-0.5 text-[10px] text-muted-foreground">
        {log.client_id}
        {log.service ? ` · ${log.service}` : ""}
      </div>
    </li>
  );
}
